import { create } from 'zustand';
import { WorkflowStatus, WorkflowStatusCategory } from '@/types';

/**
 * Workflow Status state store using Zustand
 * Manages workflow status-related state for the project management app
 */

interface WorkflowStatusState {
  // Workflow statuses for current workspace
  workflowStatuses: WorkflowStatus[];
  defaultWorkflowStatus: WorkflowStatus | null;
  doneStatuses: WorkflowStatus[];

  // Loading state
  isLoading: boolean;
}

interface WorkflowStatusActions {
  // Workflow status list actions
  setWorkflowStatuses: (workflowStatuses: WorkflowStatus[]) => void;
  addWorkflowStatus: (workflowStatus: WorkflowStatus) => void;
  updateWorkflowStatus: (
    workflowStatusId: string,
    updates: Partial<WorkflowStatus>
  ) => void;
  removeWorkflowStatus: (workflowStatusId: string) => void;

  // Default and done status actions
  setDefaultWorkflowStatus: (workflowStatus: WorkflowStatus | null) => void;
  setDoneStatuses: (doneStatuses: WorkflowStatus[]) => void;

  // Reorder actions
  reorderWorkflowStatuses: (orderedIds: string[]) => void;

  // Loading actions
  setLoading: (isLoading: boolean) => void;

  // Clear all workflow status data
  clearWorkflowStatuses: () => void;
}

type WorkflowStatusStore = WorkflowStatusState & WorkflowStatusActions;

export const useWorkflowStatusStore = create<WorkflowStatusStore>()((set) => ({
  // Initial state
  workflowStatuses: [],
  defaultWorkflowStatus: null,
  doneStatuses: [],
  isLoading: false,

  // Workflow status list actions
  setWorkflowStatuses: (workflowStatuses: WorkflowStatus[]) => {
    const defaultStatus = workflowStatuses.find((ws) => ws.isDefault);
    const doneStatuses = workflowStatuses.filter((ws) => ws.isDoneStatus);

    set({
      workflowStatuses,
      defaultWorkflowStatus: defaultStatus || null,
      doneStatuses,
      isLoading: false,
    });
  },

  addWorkflowStatus: (workflowStatus: WorkflowStatus) => {
    set((state) => {
      const updatedStatuses = [...state.workflowStatuses, workflowStatus].sort(
        (a, b) => a.displayOrder - b.displayOrder
      );

      return {
        workflowStatuses: updatedStatuses,
        doneStatuses: workflowStatus.isDoneStatus
          ? [...state.doneStatuses, workflowStatus]
          : state.doneStatuses,
      };
    });
  },

  updateWorkflowStatus: (
    workflowStatusId: string,
    updates: Partial<WorkflowStatus>
  ) => {
    set((state) => {
      const updatedStatuses = state.workflowStatuses.map((status) =>
        status.workflowStatusId === workflowStatusId
          ? { ...status, ...updates }
          : status
      );

      // Update default if this was set as default
      const newDefault = updates.isDefault
        ? updatedStatuses.find((ws) => ws.workflowStatusId === workflowStatusId)
        : state.defaultWorkflowStatus?.workflowStatusId === workflowStatusId
          ? { ...state.defaultWorkflowStatus, ...updates }
          : state.defaultWorkflowStatus;

      // Update done statuses
      const newDoneStatuses = updatedStatuses.filter((ws) => ws.isDoneStatus);

      return {
        workflowStatuses: updatedStatuses,
        defaultWorkflowStatus: newDefault || null,
        doneStatuses: newDoneStatuses,
      };
    });
  },

  removeWorkflowStatus: (workflowStatusId: string) => {
    set((state) => ({
      workflowStatuses: state.workflowStatuses.filter(
        (status) => status.workflowStatusId !== workflowStatusId
      ),
      defaultWorkflowStatus:
        state.defaultWorkflowStatus?.workflowStatusId === workflowStatusId
          ? null
          : state.defaultWorkflowStatus,
      doneStatuses: state.doneStatuses.filter(
        (status) => status.workflowStatusId !== workflowStatusId
      ),
    }));
  },

  // Default and done status actions
  setDefaultWorkflowStatus: (workflowStatus: WorkflowStatus | null) => {
    set((state) => {
      // Update isDefault flag on all workflow statuses
      const updatedStatuses = state.workflowStatuses.map((ws) => ({
        ...ws,
        isDefault: workflowStatus
          ? ws.workflowStatusId === workflowStatus.workflowStatusId
          : false,
      }));

      return {
        workflowStatuses: updatedStatuses,
        defaultWorkflowStatus: workflowStatus,
      };
    });
  },

  setDoneStatuses: (doneStatuses: WorkflowStatus[]) => {
    set({ doneStatuses });
  },

  // Reorder actions
  reorderWorkflowStatuses: (orderedIds: string[]) => {
    set((state) => {
      const reorderedStatuses = orderedIds
        .map((id, index) => {
          const status = state.workflowStatuses.find(
            (ws) => ws.workflowStatusId === id
          );
          return status ? { ...status, displayOrder: index } : null;
        })
        .filter((ws): ws is WorkflowStatus => ws !== null);

      return { workflowStatuses: reorderedStatuses };
    });
  },

  // Loading actions
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  // Clear all workflow status data
  clearWorkflowStatuses: () => {
    set({
      workflowStatuses: [],
      defaultWorkflowStatus: null,
      doneStatuses: [],
      isLoading: false,
    });
  },
}));

/**
 * Selector hooks for specific state slices
 */
export const useWorkflowStatuses = () =>
  useWorkflowStatusStore((state) => state.workflowStatuses);
export const useDefaultWorkflowStatus = () =>
  useWorkflowStatusStore((state) => state.defaultWorkflowStatus);
export const useDoneStatuses = () =>
  useWorkflowStatusStore((state) => state.doneStatuses);
export const useWorkflowStatusLoading = () =>
  useWorkflowStatusStore((state) => state.isLoading);

// Get workflow status by ID
export const useWorkflowStatusById = (workflowStatusId: string | undefined) =>
  useWorkflowStatusStore((state) =>
    workflowStatusId
      ? state.workflowStatuses.find(
          (ws) => ws.workflowStatusId === workflowStatusId
        )
      : undefined
  );

// Get workflow statuses by category
export const useWorkflowStatusesByCategory = (
  category: WorkflowStatusCategory
) =>
  useWorkflowStatusStore((state) =>
    state.workflowStatuses.filter((ws) => ws.category === category)
  );

// Get todo statuses
export const useTodoStatuses = () =>
  useWorkflowStatusStore((state) =>
    state.workflowStatuses.filter((ws) => ws.category === 'todo')
  );

// Get in progress statuses
export const useInProgressStatuses = () =>
  useWorkflowStatusStore((state) =>
    state.workflowStatuses.filter((ws) => ws.category === 'in_progress')
  );

export default useWorkflowStatusStore;
